import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useT, useLocale } from '../i18n/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Callout from '../components/ui/Callout';
import StatCard from '../components/ui/StatCard';
import DataTable from '../components/ui/DataTable';
import EmptyState from '../components/ui/EmptyState';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import SearchRow, { SearchInput } from '../components/ui/SearchRow';
import FinanceFormModal from '../components/finance/FinanceFormModal';
import {
  EXPENSE_CATEGORIES, INCOME_CATEGORIES, loadTransactions, nextCode, saveTransactions,
} from '../data/financeStore';
import { formatCurrency, formatDate } from '../utils/format';
import useDebounce from '../hooks/useDebounce';

const PRINT_CLASS = 'is-printing-finance';
const PERIODS = ['this_month', 'last_month', 'this_year', 'all', 'custom'];

function isoDay(d) {
  return d.toISOString().slice(0, 10);
}

/** [from, to] inclusive ISO dates for a period; null bounds mean open-ended. */
function periodRange(period, customFrom, customTo) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (period) {
    case 'this_month': return [isoDay(new Date(y, m, 1, 12)), isoDay(new Date(y, m + 1, 0, 12))];
    case 'last_month': return [isoDay(new Date(y, m - 1, 1, 12)), isoDay(new Date(y, m, 0, 12))];
    case 'this_year': return [`${y}-01-01`, `${y}-12-31`];
    case 'custom': return [customFrom || null, customTo || null];
    default: return [null, null];
  }
}

function csvCell(value) {
  const s = value == null ? '' : String(value);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Category totals for one type, largest first. */
function breakdown(rows, type) {
  const totals = {};
  rows.filter((r) => r.type === type).forEach((r) => { totals[r.category] = (totals[r.category] || 0) + r.amount; });
  return Object.entries(totals).sort((a, b) => b[1] - a[1]);
}

function ReportBody({ t, locale, rows, totals, periodLabel, generatedBy }) {
  return (
    <article className="fin-report">
      <header className="fin-report__header">
        <div className="fin-report__brand">✝ Indabo za Mariya</div>
        <h1>{t('finance.report_title')}</h1>
        <p>{periodLabel} · {t('finance.report_generated', { date: formatDate(new Date(), t), name: generatedBy })}</p>
      </header>
      <section className="fin-report__totals">
        <div><span>{t('finance.total_income')}</span><strong>{formatCurrency(totals.income, locale)}</strong></div>
        <div><span>{t('finance.total_expense')}</span><strong>{formatCurrency(totals.expense, locale)}</strong></div>
        <div><span>{t('finance.net_balance')}</span><strong>{formatCurrency(totals.income - totals.expense, locale)}</strong></div>
      </section>
      {['income', 'expense'].map((type) => {
        const items = breakdown(rows, type);
        if (!items.length) return null;
        return (
          <section key={type} className="fin-report__section">
            <h2>{t(type === 'income' ? 'finance.breakdown_income' : 'finance.breakdown_expense')}</h2>
            <table>
              <tbody>
                {items.map(([cat, amount]) => (
                  <tr key={cat}><td>{t(`finance.categories.${cat}`)}</td><td className="num">{formatCurrency(amount, locale)}</td></tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}
      <section className="fin-report__section">
        <h2>{t('finance.report_transactions')}</h2>
        <table>
          <thead>
            <tr>
              <th>{t('finance.field_date')}</th>
              <th>{t('finance.field_type')}</th>
              <th>{t('finance.field_category')}</th>
              <th>{t('finance.col_from')}</th>
              <th>{t('finance.col_to')}</th>
              <th>{t('finance.field_reference')}</th>
              <th className="num">{t('finance.field_amount')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{formatDate(r.date, t)}</td>
                <td>{t(`finance.types.${r.type}`)}</td>
                <td>{t(`finance.categories.${r.category}`)}</td>
                <td>{r.source}</td>
                <td>{r.destination}</td>
                <td>{r.reference || '—'}</td>
                <td className="num">{r.type === 'expense' ? '−' : ''}{formatCurrency(r.amount, locale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </article>
  );
}

export default function FinancePage() {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const { user } = useAuth();

  const [transactions, setTransactions] = useState(loadTransactions);
  const [query, setQuery] = useState('');
  const [type, setType] = useState('');
  const [category, setCategory] = useState('');
  const [period, setPeriod] = useState('this_year');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const debouncedQuery = useDebounce(query, 200);

  const [formOpen, setFormOpen] = useState(false);
  const [defaultType, setDefaultType] = useState('income');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => { saveTransactions(transactions); }, [transactions]);

  useEffect(() => {
    const done = () => { document.body.classList.remove(PRINT_CLASS); setPrinting(false); };
    window.addEventListener('afterprint', done);
    return () => { window.removeEventListener('afterprint', done); done(); };
  }, []);

  const [from, to] = periodRange(period, customFrom, customTo);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    return transactions
      .filter((r) => (!from || r.date >= from) && (!to || r.date <= to))
      .filter((r) => !type || r.type === type)
      .filter((r) => !category || r.category === category)
      .filter((r) => !q || [r.code, r.source, r.destination, r.reference, r.description, t(`finance.categories.${r.category}`)]
        .some((v) => String(v || '').toLowerCase().includes(q)))
      .sort((a, b) => (b.date.localeCompare(a.date)) || String(b.code).localeCompare(String(a.code)));
  }, [transactions, debouncedQuery, type, category, from, to, t]);

  const totals = useMemo(() => filtered.reduce((acc, r) => {
    acc[r.type] += r.amount;
    return acc;
  }, { income: 0, expense: 0 }), [filtered]);

  const periodLabel = period === 'custom'
    ? t('finance.period_range', { from: from ? formatDate(from, t) : '…', to: to ? formatDate(to, t) : '…' })
    : t(`finance.periods.${period}`);

  const categoryOptions = useMemo(() => {
    const cats = type === 'income' ? INCOME_CATEGORIES : type === 'expense' ? EXPENSE_CATEGORIES : [...INCOME_CATEGORIES, ...EXPENSE_CATEGORIES];
    return [{ value: '', label: t('finance.all_categories') }, ...cats.map((c) => ({ value: c, label: t(`finance.categories.${c}`) }))];
  }, [type, t]);

  const openNew = (kind) => { setEditing(null); setDefaultType(kind); setFormOpen(true); };

  const handleSubmit = (data) => {
    if (editing) {
      setTransactions((list) => list.map((r) => (r.id === editing.id ? { ...r, ...data, sample: false, updatedAt: new Date().toISOString() } : r)));
      toast.success(t('finance.toast_updated'));
    } else {
      setTransactions((list) => [
        ...list,
        {
          ...data,
          id: `fin-${Date.now()}`,
          code: nextCode(list),
          recordedBy: user ? `${user.firstName} ${user.lastName}` : '—',
          createdAt: new Date().toISOString(),
        },
      ]);
      toast.success(t(data.type === 'income' ? 'finance.toast_income' : 'finance.toast_expense'));
    }
    setFormOpen(false);
  };

  const handleDelete = () => {
    setTransactions((list) => list.filter((r) => r.id !== deleting.id));
    toast.success(t('finance.toast_deleted'));
    setDeleting(null);
  };

  const handleClearSamples = () => {
    setTransactions((list) => list.filter((r) => !r.sample));
    toast.success(t('finance.toast_samples_cleared'));
    setClearing(false);
  };

  const exportCsv = useCallback(() => {
    const header = ['code', 'field_date', 'field_type', 'field_category', 'field_amount', 'col_from', 'col_to', 'field_method', 'field_reference', 'field_description', 'col_recorded_by']
      .map((k) => (k === 'code' ? t('finance.col_code') : t(`finance.${k}`)));
    const lines = filtered.map((r) => [
      r.code, r.date, t(`finance.types.${r.type}`), t(`finance.categories.${r.category}`),
      r.type === 'expense' ? -r.amount : r.amount, r.source, r.destination,
      t(`finance.methods.${r.method}`), r.reference, r.description, r.recordedBy,
    ].map(csvCell).join(','));
    // BOM so Excel opens accents and RWF amounts correctly.
    const blob = new Blob([`﻿${[header.map(csvCell).join(','), ...lines].join('\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finance-${period}-${isoDay(new Date())}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(t('finance.toast_exported', { count: filtered.length }));
  }, [filtered, period, t, toast]);

  const handlePrint = () => {
    setPrinting(true);
    // Let the print-only report render before opening the dialog.
    setTimeout(() => {
      document.body.classList.add(PRINT_CLASS);
      window.print();
    }, 50);
  };

  const columns = [
    { key: 'date', header: t('finance.field_date'), render: (r) => formatDate(r.date, t) },
    {
      key: 'type',
      header: t('finance.field_type'),
      render: (r) => <Badge tone={r.type === 'income' ? 'active' : 'inactive'}>{t(`finance.types.${r.type}`)}</Badge>,
    },
    {
      key: 'category',
      header: t('finance.field_category'),
      render: (r) => (
        <div>
          <strong>{t(`finance.categories.${r.category}`)}</strong>
          {r.description && <div className="fin-cell-sub">{r.description}</div>}
        </div>
      ),
    },
    {
      key: 'flow',
      header: t('finance.col_flow'),
      render: (r) => (
        <div className="fin-flow">
          <span>{r.source}</span>
          <span className="fin-flow__arrow" aria-hidden>→</span>
          <span>{r.destination}</span>
        </div>
      ),
    },
    {
      key: 'method',
      header: t('finance.field_method'),
      render: (r) => (
        <div>
          {t(`finance.methods.${r.method}`)}
          {r.reference && <div className="fin-cell-sub mono">{r.reference}</div>}
        </div>
      ),
    },
    {
      key: 'amount',
      header: t('finance.field_amount'),
      headerClassName: 'num',
      cellClassName: 'num',
      render: (r) => (
        <strong className={r.type === 'income' ? 'fin-amount--in' : 'fin-amount--out'}>
          {r.type === 'income' ? '+' : '−'}{formatCurrency(r.amount, locale)}
        </strong>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (r) => (
        <div className="action-row">
          <button type="button" className="action-btn" onClick={() => { setEditing(r); setFormOpen(true); }}>{t('common.edit')}</button>
          <button type="button" className="action-btn danger" onClick={() => setDeleting(r)}>{t('common.delete')}</button>
        </div>
      ),
    },
  ];

  const incomeBreakdown = breakdown(filtered, 'income');
  const expenseBreakdown = breakdown(filtered, 'expense');
  const hasSamples = transactions.some((r) => r.sample);
  const net = totals.income - totals.expense;
  const generatedBy = user ? `${user.firstName} ${user.lastName}` : '';

  return (
    <div>
      <Callout tone="info" title={t('finance.local_title')}>
        {t('finance.local_body')}
        {hasSamples && (
          <> <button type="button" className="fin-link-btn" onClick={() => setClearing(true)}>{t('finance.clear_samples')}</button></>
        )}
      </Callout>

      <div className="fin-toolbar">
        <div className="fin-periods" role="tablist" aria-label={t('finance.period')}>
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={period === p}
              className={`fin-period-btn ${period === p ? 'is-active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {t(`finance.periods.${p}`)}
            </button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="fin-custom-range">
            <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} aria-label={t('finance.from')} />
            <span>→</span>
            <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} aria-label={t('finance.to')} />
          </div>
        )}
        <div className="fin-actions">
          <Button variant="ghost" onClick={exportCsv} disabled={!filtered.length} iconLeft="⬇">{t('finance.export_csv')}</Button>
          <Button variant="ghost" onClick={handlePrint} disabled={!filtered.length} iconLeft="🖨️">{t('finance.print_report')}</Button>
        </div>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <StatCard label={t('finance.total_income')} value={formatCurrency(totals.income, locale)} change={periodLabel} icon="📥" tone="sc4" />
        <StatCard label={t('finance.total_expense')} value={formatCurrency(totals.expense, locale)} change={periodLabel} changeTone="warn" icon="📤" tone="sc2" />
        <StatCard
          label={t('finance.net_balance')}
          value={formatCurrency(net, locale)}
          change={net >= 0 ? t('finance.surplus') : t('finance.deficit')}
          changeTone={net >= 0 ? 'success' : 'warn'}
          icon="⚖️"
          tone="sc1"
        />
      </div>

      <div className="fin-quick-add">
        <button type="button" className="fin-quick-btn fin-quick-btn--income" onClick={() => openNew('income')}>
          <span className="fin-quick-btn__icon" aria-hidden>↓</span>
          <span>
            <strong>{t('finance.record_income')}</strong>
            <small>{t('finance.record_income_hint')}</small>
          </span>
        </button>
        <button type="button" className="fin-quick-btn fin-quick-btn--expense" onClick={() => openNew('expense')}>
          <span className="fin-quick-btn__icon" aria-hidden>↑</span>
          <span>
            <strong>{t('finance.record_expense')}</strong>
            <small>{t('finance.record_expense_hint')}</small>
          </span>
        </button>
      </div>

      <SearchRow>
        <SearchInput value={query} onChange={setQuery} placeholder={t('finance.search_placeholder')} />
        <Select
          value={type}
          onChange={(e) => { setType(e.target.value); setCategory(''); }}
          options={[{ value: '', label: t('finance.all_types') }, { value: 'income', label: t('finance.types.income') }, { value: 'expense', label: t('finance.types.expense') }]}
          className="filter-select"
        />
        <Select value={category} onChange={(e) => setCategory(e.target.value)} options={categoryOptions} className="filter-select" />
      </SearchRow>

      <Card title={t('finance.ledger_title')} subtitle={t('finance.ledger_subtitle', { count: filtered.length, period: periodLabel })} padded={false}>
        <DataTable
          columns={columns}
          rows={filtered}
          empty={(
            <EmptyState
              title={t('finance.empty_title')}
              body={t('finance.empty_body')}
              action={<Button variant="gold" onClick={() => openNew('income')}>+ {t('finance.new_entry')}</Button>}
            />
          )}
        />
      </Card>

      {(incomeBreakdown.length > 0 || expenseBreakdown.length > 0) && (
        <div className="split-grid">
          {[['income', incomeBreakdown, totals.income], ['expense', expenseBreakdown, totals.expense]].map(([kind, items, total]) => (
            <Card key={kind} title={t(kind === 'income' ? 'finance.breakdown_income' : 'finance.breakdown_expense')} subtitle={periodLabel}>
              {items.length === 0 ? (
                <div className="form-hint">{t('finance.breakdown_empty')}</div>
              ) : (
                <div className="fin-bars">
                  {items.map(([cat, amount]) => (
                    <div key={cat} className="fin-bar">
                      <div className="fin-bar__label">
                        <span>{t(`finance.categories.${cat}`)}</span>
                        <strong>{formatCurrency(amount, locale)}</strong>
                      </div>
                      <div className="fin-bar__track">
                        <div className={`fin-bar__fill fin-bar__fill--${kind}`} style={{ width: `${Math.max(4, (amount / total) * 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <FinanceFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        initial={editing}
        defaultType={defaultType}
      />

      <ConfirmDialog
        isOpen={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={t('finance.delete_title')}
        body={deleting ? t('finance.delete_body', { code: deleting.code }) : ''}
        confirmLabel={t('common.delete')}
      />

      <ConfirmDialog
        isOpen={clearing}
        onClose={() => setClearing(false)}
        onConfirm={handleClearSamples}
        title={t('finance.clear_samples_title')}
        body={t('finance.clear_samples_body')}
        confirmLabel={t('finance.clear_samples')}
      />

      {printing && createPortal(
        <div className="fin-print-root" aria-hidden>
          <ReportBody t={t} locale={locale} rows={filtered} totals={totals} periodLabel={periodLabel} generatedBy={generatedBy} />
        </div>,
        document.body
      )}
    </div>
  );
}
